/**
 * devices/SessionRow.tsx — one session on the recency ladder.
 *
 * The node vocabulary is BORROWED, not invented: `.order-node--current` (the
 * breathing gold ring) and `.order-node--upcoming` (a hollow hairline ring on
 * paper) already ship for the orders timeline and the signup docket, so a
 * session list reads in the same material language as the rest of the product
 * and costs zero new CSS.
 *
 * `.order-node--done` — the STRUCK GOLD COIN — is deliberately forbidden here.
 * In this codebase gold-struck means approved/complete (it is the `.cert-seal`
 * family); using it to mark a session you are about to KILL would be a
 * vocabulary collision, and a bad one, because the same mark says "verified"
 * three screens away.
 *
 * Selection is rendered in INK, not light: the node fills navy and the rail
 * segment below it drops to 30%. A luminance glow would be invisible on a
 * low-end Android LCD at 60% brightness and is deleted outright by
 * `prefers-reduced-transparency` — i.e. the one flourish carrying the meaning
 * would be the first thing to degrade. Ink survives greyscale.
 */
import { motion } from 'framer-motion'
import type { DeviceSummary } from '../../lib/deviceSession'
import { PressScale } from '../../lib/PressScale'
import { firstSeen, lastSeen, sessionSentence } from './deviceCopy'

const EASE_OUT = [0.16, 1, 0.3, 1] as const

/** Row entrance stagger. Capped at 9 so a long list lands as a wave, not a crawl. */
export function rowDelay(index: number, still: boolean): number {
  if (still) return 0
  return 0.04 + Math.min(index, 9) * 0.055
}

/** A check that DRAWS rather than pops — the one flourish on the selection. */
export function DrawnCheck({ still }: { still: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden>
      <motion.path
        d="M4.5 12.4 9.4 17.2 19.5 7"
        stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
        initial={still ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={still ? { duration: 0 } : { duration: 0.34, ease: EASE_OUT }}
      />
    </svg>
  )
}

export type Connector = 'solid' | 'dashed' | 'none'

/**
 * The rail gutter: the node, plus the segment running down to the next node.
 * Everything in here is `aria-hidden` — it is a redundant rendering of text a
 * screen reader is already being given as a full sentence.
 */
function RailGutter({
  tone, connector, dimBelow, gapBelow, still, index,
}: {
  tone: 'current' | 'live' | 'selected' | 'slot'
  connector: Connector
  dimBelow: boolean
  gapBelow: number
  still: boolean
  index: number
}) {
  const nodeClass =
    tone === 'current' ? 'order-node--current'
      : 'order-node--upcoming'
  return (
    <span className="relative block w-[22px] self-stretch" aria-hidden>
      {connector !== 'none' && (
        <motion.span
          className="absolute left-1/2 w-px -translate-x-1/2"
          style={{
            top: 27,
            bottom: -gapBelow,
            transformOrigin: 'top',
            ...(connector === 'dashed'
              ? {
                background:
                    'repeating-linear-gradient(180deg, rgba(14,42,79,0.22) 0 3px, transparent 3px 7px)',
              }
              : { background: 'rgba(14,42,79,0.14)' }),
          }}
          initial={still ? false : { scaleY: 0, opacity: 0 }}
          animate={{ scaleY: 1, opacity: dimBelow ? 0.3 : 1 }}
          transition={still
            ? { duration: 0 }
            : { duration: 0.38, ease: EASE_OUT, delay: rowDelay(index, still) }}
        />
      )}
      <motion.span
        className={`absolute left-1/2 top-[10px] block h-3.5 w-3.5 -translate-x-1/2 rounded-full ${nodeClass}`}
        style={{
          ...(tone === 'selected' ? { background: 'rgba(14,42,79,0.22)' } : null),
          ...(tone === 'slot' ? { opacity: 0.45 } : null),
        }}
        initial={still ? false : { scale: 0.55, opacity: 0 }}
        animate={{ scale: 1, opacity: tone === 'slot' ? 0.45 : 1 }}
        transition={still
          ? { duration: 0 }
          : { duration: 0.3, ease: EASE_OUT, delay: rowDelay(index, still) }}
      />
    </span>
  )
}

function SinceLine({ device, suggested }: { device: DeviceSummary; suggested: boolean }) {
  const since = firstSeen(device.firstSeenAt)
  return (
    <span className="mt-0.5 block text-xs leading-snug text-navy-500">
      <span className="tabular">{lastSeen(device.lastSeenAt)}</span>
      {since && <span className="text-navy-500/80"> · {since}</span>}
      {suggested && (
        <span className="text-navy-500/80"> · least recently used</span>
      )}
    </span>
  )
}

/* ─────────────────────────── choose mode ───────────────────────────────── */

/**
 * A selectable session. WAI-ARIA radiogroup member: roving tabindex, arrow
 * keys handled by the rail, `aria-checked` on the button itself.
 */
export function ChoiceRow({
  device, index, selected, suggested, disabled, connector, gapAbove, gapBelow, still,
  onSelect, onKeyDown, innerRef,
}: {
  device: DeviceSummary
  index: number
  selected: boolean
  suggested: boolean
  disabled: boolean
  connector: Connector
  gapAbove: number
  gapBelow: number
  still: boolean
  onSelect: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  innerRef: (el: HTMLButtonElement | null) => void
}) {
  return (
    <motion.button
      ref={innerRef}
      type="button"
      role="radio"
      aria-checked={selected}
      // Roving tabindex: exactly ONE row is in the tab order, and the arrow keys
      // move within the group. Three tab stops for one decision is the pattern
      // that makes keyboard users think a screen has more controls than it does.
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      disabled={disabled}
      data-testid="device-gate-option"
      data-selected={selected ? 'true' : 'false'}
      aria-label={sessionSentence(device, { suggested })}
      style={{ marginTop: gapAbove }}
      className={[
        'group relative grid w-full grid-cols-[22px_minmax(0,1fr)_auto] items-stretch gap-3',
        'min-h-[56px] rounded-md px-2.5 py-2.5 text-left',
        'transition-[box-shadow,background-color,border-color] duration-default',
        // Hairline on paper, not white-on-white. The rows used to be
        // `bg-white/55 border-white/70`, which only read at all because the
        // card behind them was translucent glass; against the near-solid paper
        // this card now uses they were invisible except by accident.
        'border hover:bg-navy-900/[0.02]',
        selected ? 'border-gold-500 bg-gold-050/50 shadow-elev-1' : 'border-line bg-transparent',
        'disabled:opacity-60',
      ].join(' ')}
      initial={still ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={still ? { duration: 0 } : { duration: 0.32, ease: EASE_OUT, delay: rowDelay(index, still) }}
      whileTap={still ? undefined : { scale: 0.985 }}
    >
      <RailGutter
        tone={selected ? 'selected' : device.current ? 'current' : 'live'}
        connector={connector}
        dimBelow={selected}
        gapBelow={gapBelow}
        still={still}
        index={index}
      />
      <span className="min-w-0 self-center" aria-hidden>
        <span className="block truncate text-sm font-medium text-navy-900">{device.label}</span>
        <SinceLine device={device} suggested={suggested} />
      </span>
      {/* Reserved at rest, not DRAWN at rest. A hollow ring here plus the rail
          node on the left gave every row two identical empty circles — it read
          as two radio buttons per session (confirmed in the browser). The box
          still occupies its 24px so selecting cannot shift the row. */}
      <span
        className={[
          'flex h-6 w-6 shrink-0 self-center items-center justify-center rounded-full border',
          'transition-colors duration-micro',
          selected ? 'border-gold-500 bg-gold-500 text-navy-900' : 'border-transparent text-transparent',
        ].join(' ')}
        aria-hidden
      >
        {selected && <DrawnCheck still={still} />}
      </span>
    </motion.button>
  )
}

/* ─────────────────────────── manage mode ───────────────────────────────── */

/** A session in settings: read-only, with a deliberate trailing sign-out. */
export function ManageRow({
  device, index, connector, gapAbove, gapBelow, still, busy, note, onRevoke, onRetry,
}: {
  device: DeviceSummary
  index: number
  connector: Connector
  gapAbove: number
  gapBelow: number
  still: boolean
  busy: boolean
  /** Inline outcome under the row — quiet for `gone`, danger for a failure. */
  note: { tone: 'quiet' | 'danger'; text: string } | null
  onRevoke: () => void
  onRetry: () => void
}) {
  return (
    <motion.li
      data-testid="device-session-row"
      data-current={device.current ? 'true' : 'false'}
      style={{ marginTop: gapAbove }}
      className={[
        'relative grid grid-cols-[22px_minmax(0,1fr)_auto] items-stretch gap-3',
        'min-h-[56px] rounded-md px-2.5 py-2.5',
        busy ? 'pointer-events-none opacity-60' : '',
      ].join(' ')}
      initial={still ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: busy ? 0.6 : 1, y: 0 }}
      exit={still ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
      transition={still ? { duration: 0 } : { duration: 0.32, ease: EASE_OUT, delay: rowDelay(index, still) }}
      layout={still ? false : 'position'}
    >
      <RailGutter
        tone={device.current ? 'current' : 'live'}
        connector={connector}
        dimBelow={false}
        gapBelow={gapBelow}
        still={still}
        index={index}
      />
      <span className="min-w-0 self-center">
        <span className="flex items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-navy-900">{device.label}</span>
          {device.current && (
            <span className="shrink-0 rounded-full bg-navy-900/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-500">
              This device
            </span>
          )}
        </span>
        <SinceLine device={device} suggested={false} />
        {note && (
          <span
            role="status"
            className={`mt-1 block text-xs ${note.tone === 'danger' ? 'text-danger' : 'text-navy-500'}`}
          >
            {note.text}
            {note.tone === 'danger' && (
              <button
                type="button"
                onClick={onRetry}
                className="ml-2 min-h-[24px] underline underline-offset-2 hover:text-navy-900"
              >
                Try again
              </button>
            )}
          </span>
        )}
      </span>
      {!device.current && (
        <PressScale
          feel="dim"
          onClick={onRevoke}
          disabled={busy}
          data-testid="device-session-revoke"
          className="btn-ghost h-11 shrink-0 self-center px-3 text-xs"
          aria-label={`Sign out ${device.label}`}
        >
          {busy ? 'Signing out…' : 'Sign out'}
        </PressScale>
      )}
    </motion.li>
  )
}

/** An unused slot, so `limit − used` is something you can count. */
export function FreeSlotRow({
  index, gapAbove, still, connector, gapBelow,
}: {
  index: number
  gapAbove: number
  still: boolean
  connector: Connector
  gapBelow: number
}) {
  return (
    <motion.li
      data-testid="device-session-slot"
      style={{ marginTop: gapAbove }}
      className="relative grid min-h-[44px] grid-cols-[22px_minmax(0,1fr)] items-stretch gap-3 rounded-md px-2.5 py-2"
      initial={still ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={still ? { duration: 0 } : { duration: 0.28, ease: EASE_OUT, delay: rowDelay(index, still) }}
    >
      <RailGutter
        tone="slot" connector={connector} dimBelow={false}
        gapBelow={gapBelow} still={still} index={index}
      />
      <span className="self-center text-xs text-navy-500/80">Free session</span>
    </motion.li>
  )
}
